import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  Inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { ToastComponent } from '../../shared/toast/toast.component';
import { API_ENDPOINTS } from '../../shared/constants/apiEnpoints';
import { MAX_INT_32 } from '../../shared/constants/values';
import { finalize } from 'rxjs/operators';
import { DatePipe } from '@angular/common';
import moment from 'moment';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { OKTA_AUTH } from '@okta/okta-angular';
import OktaAuth from '@okta/okta-auth-js';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import { computeFitSettings } from '../../shared/export-to-pdf/pdfHelpers';
const vfs = (pdfFonts as any).pdfMake?.vfs ?? (pdfFonts as any).vfs;
(pdfMake as any).vfs = vfs;

export type ReportFormat = 'date' | 'month' | 'provider';

interface NetCollectionRow {
  practiceCode: string;
  groupKey: string;
  amountCollection: number;
  amountCollectionAdjustment: number;
  netAmount: number;
}

@Component({
  selector: 'app-net-collection-report',
  templateUrl: './net-collection-report.component.html',
  styleUrl: './net-collection-report.component.scss',
  providers: [DatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NetCollectionReportComponent implements OnInit, AfterViewInit {
  @ViewChild(ToastComponent) toastComponent!: ToastComponent;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  dataSource = new MatTableDataSource<NetCollectionRow>();
  isLoading = false;
  showErrorCard = false;
  errorTitle = '';
  errorMessage = '';
  isDropdownOpen = false;
  isPmsDropdownOpen = false;
  isPracticeDropdownOpen = false;
  filtersChanged = false;
  reportRan = false;

  // Filters
  pmsList: any[] = [];
  allPractices: any[] = [];
  selectedPmsIds: any[] = [];
  selectedPracticeIds: any[] = [];
  pmsSearch = '';
  practiceSearch = '';
  startDate = '';
  endDate = '';
  reportFormat: ReportFormat = 'date';

  get practiceOptions(): any[] {
    const base = this.selectedPmsIds.length
      ? this.allPractices.filter(p => this.selectedPmsIds.some(s => s.id === p.pmsId))
      : this.allPractices;
    const q = this.practiceSearch.trim().toLowerCase();
    return q ? base.filter(p => p.name.toLowerCase().includes(q)) : base;
  }

  get filteredPmsList(): any[] {
    const q = this.pmsSearch.trim().toLowerCase();
    return q ? this.pmsList.filter(p => p.name.toLowerCase().includes(q)) : this.pmsList;
  }

  // Applied state (set only when Run Report is clicked)
  appliedFormat: ReportFormat = 'date';

  // Pagination
  pageNumber = 1;
  pageSize = 50;
  totalRecords = 0;
  readonly pageSizeOptions = [25, 50, 100];

  readonly FORMAT_OPTIONS: { value: ReportFormat; label: string }[] = [
    { value: 'date', label: 'Date-wise' },
    { value: 'month', label: 'Month-wise' },
    { value: 'provider', label: 'Provider-wise' },
  ];

  get displayedColumns(): string[] {
    return ['practiceCode', 'groupKey', 'amountCollection', 'amountCollectionAdjustment', 'netAmount'];
  }

  get groupKeyLabel(): string {
    switch (this.appliedFormat) {
      case 'month': return 'Month';
      case 'provider': return 'Provider';
      default: return 'Date';
    }
  }

  markFiltersChanged(): void {
    if (this.dataSource.data.length > 0) {
      this.filtersChanged = true;
      this.cdr.markForCheck();
    }
  }

  constructor(
    private http: HttpClient,
    private cdr: ChangeDetectorRef,
    @Inject(OKTA_AUTH) private oktaAuth: OktaAuth
  ) {}

  ngAfterViewInit(): void {
    this.paginator.page.subscribe((event: PageEvent) => {
      this.pageNumber = event.pageIndex + 1;
      this.pageSize = event.pageSize;
      this.fetchReport(this.pageNumber);
    });
  }

  ngOnInit(): void {
    const today = new Date();
    const oneMonthAgo = new Date(today);
    oneMonthAgo.setMonth(today.getMonth() - 1);
    this.startDate = oneMonthAgo.toISOString().split('T')[0];
    this.endDate = today.toISOString().split('T')[0];
    this.fetchPmsOptions();
    this.fetchPractices();
  }

  fetchPmsOptions(): void {
    this.http.get<any>(API_ENDPOINTS.PMS_MASTER, { params: { pageSize: MAX_INT_32 } }).subscribe({
      next: (res) => {
        this.pmsList = (res.data || [])
          .filter((p: any) => p.isActive !== false)
          .map((p: any) => ({ id: p.id, name: `${p.name} ${p.version}`.trim(), checked: false }))
          .sort((a: any, b: any) => a.name.localeCompare(b.name));
        this.cdr.markForCheck();
      },
      error: () => {}
    });
  }

  fetchPractices(): void {
    this.http.get<any>(API_ENDPOINTS.GETALLACTIVELIVEPRACTICES).subscribe({
      next: (res) => {
        this.allPractices = (res.data || []).map((p: any) => ({
          id: p.id,
          name: `${p.practiceCode}${p.pmsName ? ' (' + p.pmsName + ')' : ''}`,
          pmsId: p.pmsId,
        }));
        this.cdr.markForCheck();
      },
      error: () => {}
    });
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.isPmsDropdownOpen = false;
    this.isPracticeDropdownOpen = false;
    this.isDropdownOpen = false;
    this.cdr.markForCheck();
  }

  togglePms(option: any): void {
    const idx = this.selectedPmsIds.findIndex((p: any) => p.id === option.id);
    idx === -1 ? this.selectedPmsIds.push(option) : this.selectedPmsIds.splice(idx, 1);
    // deselect practices that no longer belong to selected PMS
    if (this.selectedPmsIds.length) {
      this.selectedPracticeIds = this.selectedPracticeIds.filter(sp =>
        this.allPractices.some(ap => ap.id === sp.id && this.selectedPmsIds.some(s => s.id === ap.pmsId))
      );
    }
    this.markFiltersChanged();
  }

  isPmsSelected(id: any): boolean {
    return this.selectedPmsIds.some((p: any) => p.id === id);
  }

  togglePractice(option: any): void {
    const idx = this.selectedPracticeIds.findIndex((p: any) => p.id === option.id);
    idx === -1 ? this.selectedPracticeIds.push(option) : this.selectedPracticeIds.splice(idx, 1);
    this.markFiltersChanged();
  }

  isPracticeSelected(id: any): boolean {
    return this.selectedPracticeIds.some((p: any) => p.id === id);
  }

  get pmsFilterLabel(): string {
    if (!this.selectedPmsIds.length) return 'All PMS';
    return this.selectedPmsIds.length === 1 ? this.selectedPmsIds[0].name : `${this.selectedPmsIds[0].name} (+${this.selectedPmsIds.length - 1})`;
  }

  get practiceFilterLabel(): string {
    if (!this.selectedPracticeIds.length) return 'All Practices';
    return this.selectedPracticeIds.length === 1 ? this.selectedPracticeIds[0].name : `${this.selectedPracticeIds[0].name} (+${this.selectedPracticeIds.length - 1})`;
  }

  buildParams(): any {
    const params: any = {
      startDate: this.startDate,
      endDate: this.endDate,
      monthWise: this.reportFormat === 'month',
      providerWise: this.reportFormat === 'provider',
      pageNumber: this.pageNumber,
      pageSize: this.pageSize,
    };
    const pmsIds = this.selectedPmsIds.filter((p: any) => p.id !== 'All').map((p: any) => p.id);
    if (pmsIds.length) params['pmsIds'] = pmsIds.join(',');
    if (this.selectedPracticeIds.length) params['practiceIds'] = this.selectedPracticeIds.map((p: any) => p.id).join(',');
    return params;
  }

  fetchReport(page = 1): void {
    if (!this.startDate || !this.endDate) return;
    this.appliedFormat = this.reportFormat;
    this.filtersChanged = false;
    this.reportRan = false;
    this.pageNumber = page;
    this.isLoading = true;
    this.showErrorCard = false;
    this.cdr.markForCheck();

    this.http.get<any>(API_ENDPOINTS.NET_COLLECTION, { params: this.buildParams() }).pipe(
      finalize(() => { this.isLoading = false; this.cdr.markForCheck(); })
    ).subscribe({
      next: (res) => {
        this.totalRecords = res.totalRecordsCount ?? 0;
        if (this.paginator) {
          this.paginator.length = this.totalRecords;
          this.paginator.pageIndex = this.pageNumber - 1;
        }
        this.dataSource.data = (res.data || []).map((r: any) => ({
          practiceCode: r.practiceCode,
          groupKey: this.resolveGroupKey(r),
          amountCollection: r.prodAmount ?? 0,
          amountCollectionAdjustment: r.adjAmount ?? 0,
          netAmount: r.totalAmount ?? 0,
        }));
        this.reportRan = true;
        this.cdr.markForCheck();
      },
      error: (err) => this.handleError(err),
    });
  }

  onPageSizeChange(): void {
    this.fetchReport(1);
  }

  resetFilters(): void {
    const today = new Date();
    const oneMonthAgo = new Date(today);
    oneMonthAgo.setMonth(today.getMonth() - 1);
    this.startDate = oneMonthAgo.toISOString().split('T')[0];
    this.endDate = today.toISOString().split('T')[0];
    this.selectedPmsIds = [];
    this.selectedPracticeIds = [];
    this.pmsSearch = '';
    this.practiceSearch = '';
    this.reportFormat = 'date';
    this.filtersChanged = false;
    this.reportRan = false;
    this.dataSource.data = [];
    this.totalRecords = 0;
    this.cdr.markForCheck();
  }

  private resolveGroupKey(r: any): string {
    if (this.appliedFormat === 'provider') return r.providerId ?? '';
    if (this.appliedFormat === 'month') return r.month ?? '';
    return r.transactionEntryDate ? r.transactionEntryDate.split('T')[0] : (r.month ?? '');
  }

  private buildAllDataParams(): any {
    const params: any = {
      startDate: this.startDate,
      endDate: this.endDate,
      monthWise: this.appliedFormat === 'month',
      providerWise: this.appliedFormat === 'provider',
      pageNumber: 1,
      pageSize: MAX_INT_32,
    };
    const pmsIds = this.selectedPmsIds.filter((p: any) => p.id !== 'All').map((p: any) => p.id);
    if (pmsIds.length) params['pmsIds'] = pmsIds.join(',');
    if (this.selectedPracticeIds.length) params['practiceIds'] = this.selectedPracticeIds.map((p: any) => p.id).join(',');
    return params;
  }

  private writeCollectionExcel(data: NetCollectionRow[]): void {
    const workbook = new ExcelJS.Workbook();
    const ws = workbook.addWorksheet('Net Collection');
    ws.addRow(['Practice Code', this.groupKeyLabel, 'Collection Amount', 'Collection Adjustment Amount', 'Net Amount']);
    data.forEach(r => ws.addRow([r.practiceCode, r.groupKey, r.amountCollection, r.amountCollectionAdjustment, r.netAmount]));
    ws.columns.forEach(c => c.width = 26);
    workbook.xlsx.writeBuffer().then(buf => {
      saveAs(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
        `Net_Collection_Report_${moment().format('YYYY-MM-DD-HH-mm-ss')}.xlsx`);
    });
  }

  exportToExcel(): void {
    const data = this.dataSource.data;
    if (!data?.length) {
      this.toastComponent?.showToast({ severity: 'warn', title: 'Warning', message: 'No data to export.', duration: 3000 });
      return;
    }
    this.writeCollectionExcel(data);
    this.isDropdownOpen = false;
  }

  exportAllToExcel(): void {
    if (!this.reportRan) {
      this.toastComponent?.showToast({ severity: 'warn', title: 'Warning', message: 'Run the report first.', duration: 3000 });
      return;
    }
    this.isLoading = true;
    this.isDropdownOpen = false;
    this.cdr.markForCheck();
    this.http.get<any>(API_ENDPOINTS.NET_COLLECTION, { params: this.buildAllDataParams() }).pipe(
      finalize(() => { this.isLoading = false; this.cdr.markForCheck(); })
    ).subscribe({
      next: (res) => {
        const rows: NetCollectionRow[] = (res.data || []).map((r: any) => ({
          practiceCode: r.practiceCode,
          groupKey: this.resolveGroupKey(r),
          amountCollection: r.prodAmount ?? 0,
          amountCollectionAdjustment: r.adjAmount ?? 0,
          netAmount: r.totalAmount ?? 0,
        }));
        if (!rows.length) {
          this.toastComponent?.showToast({ severity: 'warn', title: 'Warning', message: 'No data to export.', duration: 3000 });
          return;
        }
        this.writeCollectionExcel(rows);
      },
      error: (err) => this.handleError(err),
    });
  }

  handleError(error: any): void {
    this.showErrorCard = true;
    this.errorTitle = error?.error?.errorMessage || 'Error';
    this.errorMessage = error?.error?.detailedErrorMessage || 'An unexpected error occurred.';
    this.cdr.markForCheck();
  }

  closeErrorCard(): void {
    this.showErrorCard = false;
    this.cdr.markForCheck();
  }

  formatCurrency(value: number): string {
    return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(value ?? 0);
  }

  exportDataToPDF(): void {
    const data = this.dataSource.data;
    if (!data?.length) {
      this.toastComponent?.showToast({ severity: 'warn', title: 'Warning', message: 'No data to export.', duration: 3000 });
      return;
    }
    this.buildAndDownloadCollectionPDF(data as any[]);
    this.isDropdownOpen = false;
  }

  exportAllDataToPDF(): void {
    if (!this.reportRan) {
      this.toastComponent?.showToast({ severity: 'warn', title: 'Warning', message: 'Run the report first.', duration: 3000 });
      return;
    }
    this.isLoading = true;
    this.isDropdownOpen = false;
    this.cdr.markForCheck();
    this.http.get<any>(API_ENDPOINTS.NET_COLLECTION, { params: this.buildAllDataParams() }).pipe(
      finalize(() => { this.isLoading = false; this.cdr.markForCheck(); })
    ).subscribe({
      next: (res) => {
        const rows: NetCollectionRow[] = (res.data || []).map((r: any) => ({
          practiceCode: r.practiceCode,
          groupKey: this.resolveGroupKey(r),
          amountCollection: r.prodAmount ?? 0,
          amountCollectionAdjustment: r.adjAmount ?? 0,
          netAmount: r.totalAmount ?? 0,
        }));
        if (!rows.length) {
          this.toastComponent?.showToast({ severity: 'warn', title: 'Warning', message: 'No data to export.', duration: 3000 });
          return;
        }
        this.buildAndDownloadCollectionPDF(rows);
      },
      error: (err) => this.handleError(err),
    });
  }

  private buildAndDownloadCollectionPDF(data: any[]): void {
    const title = `Net Collection Report — ${this.groupKeyLabel}`;
    const subtitle = `Date Range: ${this.startDate} — ${this.endDate}`;
    const colHeaders = ['PRACTICE CODE', this.groupKeyLabel.toUpperCase(), 'COLLECTION AMOUNT', 'COLLECTION ADJUSTMENT AMOUNT', 'NET AMOUNT'];
    const fields = ['practiceCode', 'groupKey', 'amountCollection', 'amountCollectionAdjustment', 'netAmount'];
    const margins: [number, number, number, number] = [40, 60, 40, 40];
    const targetPadding = 2.5;
    const lineHeight = 0.9;

    const { fontSize, pageSize } = computeFitSettings(colHeaders, fields, data, 'A4', 'landscape', margins, 10, 8, targetPadding);

    const headerRow = colHeaders.map(text => ({
      text, bold: true, color: '#0D47A1', fillColor: '#E3F2FD',
      margin: [0, targetPadding, 0, targetPadding], noWrap: true
    }));

    const bodyRows = data.map(r => [
      { text: r.practiceCode, margin: [0, targetPadding, 0, targetPadding], lineHeight },
      { text: r.groupKey, margin: [0, targetPadding, 0, targetPadding], lineHeight },
      { text: this.formatCurrency(r.amountCollection), alignment: 'right', margin: [0, targetPadding, 0, targetPadding], lineHeight },
      { text: this.formatCurrency(r.amountCollectionAdjustment), alignment: 'right', color: r.amountCollectionAdjustment < 0 ? '#c62828' : undefined, margin: [0, targetPadding, 0, targetPadding], lineHeight },
      { text: this.formatCurrency(r.netAmount), alignment: 'right', bold: true, color: r.netAmount >= 0 ? '#2e7d32' : '#c62828', margin: [0, targetPadding, 0, targetPadding], lineHeight },
    ]);

    const now = new Date();
    const generatedAt = `${now.toLocaleDateString()} ${now.toLocaleTimeString()}`;

    const docDef: any = {
      info: { title, subject: `${title} (${generatedAt})` },
      pageSize, pageOrientation: 'landscape', pageMargins: margins,
      defaultStyle: { fontSize, lineHeight },
      watermark: { text: 'Dentalcorp', color: '#777', opacity: 0.12, angle: 35, bold: true },
      header: (currentPage: number, pageCount: number) => ({
        columns: [{ text: title, style: 'headerLeft', color: '#1976D2' }, { text: `Page ${currentPage} of ${pageCount}`, alignment: 'right', style: 'headerRight', color: '#555' }],
        margin: [40, 20, 40, 0]
      }),
      footer: () => ({ columns: [{ text: `Generated: ${generatedAt}`, alignment: 'left', color: '#444' }, { text: subtitle, alignment: 'right', color: '#444' }], margin: [40, 10, 40, 0], fontSize: 9 }),
      content: [
        { text: title, style: 'title', color: '#0D47A1', margin: [0, 0, 0, 4] },
        { text: subtitle, fontSize: 9, color: '#555', margin: [0, 0, 0, 8] },
        {
          table: { headerRows: 1, widths: fields.map(() => 'auto'), body: [headerRow, ...bodyRows] },
          layout: {
            paddingLeft: () => targetPadding, paddingRight: () => targetPadding,
            paddingTop: () => targetPadding, paddingBottom: () => targetPadding,
            hLineWidth: (i: number, node: any) => (i === 0 || i === node.table.body.length) ? 0.8 : 0.4,
            vLineWidth: () => 0.4,
            hLineColor: (i: number, node: any) => (i === 0 || i === node.table.body.length) ? '#B0BEC5' : '#CFD8DC',
            vLineColor: () => '#CFD8DC'
          }
        }
      ],
      styles: { title: { fontSize: Math.max(12, fontSize + 2), bold: true }, headerLeft: { fontSize: 9 }, headerRight: { fontSize: 9 } }
    };

    pdfMake.createPdf(docDef).download(`Net_Collection_Report_${moment().format('YYYY-MM-DD-HH-mm-ss')}.pdf`);
  }
}
